import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement
} from "react";
import { IconCamera, IconDownload, IconFileTypePdf } from "@tabler/icons-react";
import { Drawer } from "../drawer/Drawer";
import { DrawerInspectorDrillLayoutContext } from "../drawer/DrawerInspectorDrillLayoutContext";
import { DrawerInspectorNavRow, DrawerInspectorNavRowList } from "../drawer/DrawerInspectorNavRow";
import { IconControlButton } from "../rail/IconControlButton";
import { RailPanelHeader } from "../rail/IconRail";
import styles from "./ViewerTopNav.module.css";

/** Bottom inset so the export sheet clears the handheld module strip. */
const VIEWER_EXPORT_DRAWER_BOTTOM_INSET_PX = 64;

export interface ViewerExportMenuProps {
  exportButtonId: string;
  isMobile: boolean;
  onExportScreenshot: () => void;
  /** Reserved for PDF export; optional until implemented. */
  onExportPdf?: () => void;
  /**
   * Production `/viewer` handheld: open state is lifted to `RootLayout` so the
   * sheet survives nav re-renders.
   */
  mobileMenuOpen?: boolean;
  onMobileMenuOpenChange?: (open: boolean) => void;
}

export function ViewerExportMenu({
  exportButtonId,
  isMobile,
  onExportScreenshot,
  onExportPdf,
  mobileMenuOpen,
  onMobileMenuOpenChange
}: ViewerExportMenuProps): ReactElement {
  const [internalOpen, setInternalOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const controlledMobile = isMobile && onMobileMenuOpenChange != null;
  const open = controlledMobile ? Boolean(mobileMenuOpen) : internalOpen;

  const setOpenUnified = useCallback(
    (next: boolean): void => {
      if (controlledMobile) {
        onMobileMenuOpenChange(next);
      } else {
        setInternalOpen(next);
      }
    },
    [controlledMobile, onMobileMenuOpenChange]
  );

  const close = useCallback((): void => {
    setOpenUnified(false);
  }, [setOpenUnified]);

  useEffect(() => {
    if (!isMobile) {
      setInternalOpen(false);
      onMobileMenuOpenChange?.(false);
    }
  }, [isMobile, onMobileMenuOpenChange]);

  useEffect(() => {
    if (!open || isMobile) return;
    function onDocMouseDown(e: MouseEvent): void {
      const root = wrapRef.current;
      if (!root?.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") close();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, isMobile, close]);

  function handlePdf(): void {
    close();
    onExportPdf?.();
  }

  function handleScreenshot(): void {
    close();
    onExportScreenshot();
  }

  return (
    <div className={styles.exportMenuWrap} ref={wrapRef}>
      <IconControlButton
        id={exportButtonId}
        icon={IconDownload}
        aria-label="Export"
        aria-haspopup="menu"
        aria-expanded={open}
        active={open}
        onClick={() => setOpenUnified(!open)}
      />
      {!isMobile && open ? (
        <div
          className={`viewer-island ${styles.exportDropdown}`}
          role="menu"
          aria-label="Export options"
        >
          <button
            type="button"
            role="menuitem"
            className={styles.exportItem}
            onClick={handlePdf}
          >
            <IconFileTypePdf size={18} stroke={1.5} aria-hidden />
            <span>Download PDF report</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.exportItem}
            onClick={handleScreenshot}
          >
            <IconCamera size={18} stroke={1.5} aria-hidden />
            <span>Save screenshot</span>
          </button>
        </div>
      ) : null}
      {isMobile ? (
        <Drawer
          open={open}
          onClose={close}
          bottomChromeInsetPx={VIEWER_EXPORT_DRAWER_BOTTOM_INSET_PX}
          sheetHeading="Export"
          controls={
            <RailPanelHeader
              icon={<IconDownload size={20} stroke={1.5} />}
              label="Export"
              onClose={close}
            />
          }
        >
          <DrawerInspectorDrillLayoutContext.Provider value={true}>
            <DrawerInspectorNavRowList>
              <DrawerInspectorNavRow
                label="Download PDF report"
                showChevron={false}
                onNavigate={handlePdf}
              />
              <DrawerInspectorNavRow
                label="Save screenshot"
                showChevron={false}
                onNavigate={handleScreenshot}
              />
            </DrawerInspectorNavRowList>
          </DrawerInspectorDrillLayoutContext.Provider>
        </Drawer>
      ) : null}
    </div>
  );
}
